import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Autocomplete, TextField, Chip,
  Button, FormControl, Select, MenuItem, Dialog, DialogContent, CircularProgress
} from '@mui/material';
import { useParams, useLocation } from 'react-router-dom';
import CustomAlert from '../../commun/CustomAlert';
import AjouterTalentModal from '../../commun/AddTalentModal';
import AddIcon from '@mui/icons-material/Add';
import EuroIcon from '@mui/icons-material/Euro';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import './SendCandidats.css';

const UPLOADS_URL = process.env.REACT_APP_API_BASE_URL || 'https://server.cooptaleo.com';

function getStatutLabel(statut) {
  if (!statut) return "";
  if (statut === "Qualifie attente controle") return "Talent qualifié";
  if (statut.toLowerCase() === "a traiter" || statut.toLowerCase() === "à traiter") return "Talent à traiter";
  return statut;
}

const DEVISES = [
  { value: "EUR", label: "EUR" },
  { value: "MAD", label: "MAD" },
  { value: "USD", label: "USD" },
];


const SendCandidats = () => {
  const { id } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const preselectedTalentId = params.get('t');

  const [mission, setMission] = useState(location.state?.mission || null);
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const userId = localStorage.getItem("userId");

  const [talents, setTalents] = useState([]);
  const [selected, setSelected] = useState([]);
  const [generatedCVs, setGeneratedCVs] = useState({});
  const [selectedCVs, setSelectedCVs] = useState({});
  const [inputDevises, setInputDevises] = useState({});
  const [inputTarifs, setInputTarifs] = useState({});
  const [tarifErrors, setTarifErrors] = useState({});
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [openModal, setOpenModal] = useState(false);
  const [envoyes, setEnvoyes] = useState([]);
  const [iaModal, setIaModal] = useState({ open: false, talent: null });
  const [autoGenLoading, setAutoGenLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [suggestedTalentIds, setSuggestedTalentIds] = useState([]);

  // Etat pour popup recap
  const [openRecap, setOpenRecap] = useState(false);
  const [recapTalents, setRecapTalents] = useState([]);

  useEffect(() => {
    if (!mission && id) {
      fetch(`${API_BASE}/api/missions/${id}`)
        .then(res => res.json())
        .then(setMission)
        .catch(console.error);
    }
  }, [id, mission, API_BASE]);

  useEffect(() => {
    fetch(`${API_BASE}/api/recruteur/get-talents?recruteurId=${userId}`)
      .then(res => res.json())
      .then(data => setTalents(Array.isArray(data) ? data : []))
      .catch(console.error);
  }, [userId, API_BASE]);

  useEffect(() => {
    if (mission?._id && userId) {
      fetch(`${API_BASE}/api/candidatures/envoyees?missionId=${mission._id}&recruteurId=${userId}`)
        .then(res => res.json())
        .then(setEnvoyes)
        .catch(console.error);
    }
  }, [mission, userId, API_BASE]);

  // Pré-sélection automatique via URL ?t=<id>
  useEffect(() => {
    if (preselectedTalentId && talents.length > 0) {
      const exists = talents.some(t => t._id === preselectedTalentId);
      if (exists && !selected.includes(preselectedTalentId) && !envoyes.includes(preselectedTalentId)) {
        setSelected(prev => [...prev, preselectedTalentId]);
      }
    }
  }, [preselectedTalentId, talents, selected, envoyes]);

  useEffect(() => {
    const fetchCVs = async () => {
      const cvMap = {};
      await Promise.all(
        talents.map(async (t) => {
          const res = await fetch(`${API_BASE}/api/cv-generated/list/${t._id}`);
          const data = await res.json();
          if (data.status === 'success') {
            cvMap[t._id] = data.data;
          }
        })
      );
      setGeneratedCVs(cvMap);
    };
    if (talents.length > 0) fetchCVs();
  }, [talents, API_BASE]);

  useEffect(() => {
    talents.forEach(talent => {
      const cvs = generatedCVs[talent._id] || [];
      if (cvs.length > 0) {
        const sortedCVs = [...cvs].sort((a, b) =>
          new Date(b.generatedAt || b.date || 0) - new Date(a.generatedAt || a.date || 0)
        );
        const defaultCvId = sortedCVs[0]._id;
        if (!selectedCVs[talent._id]) {
          setSelectedCVs(prev => ({ ...prev, [talent._id]: defaultCvId }));
        }
      }
    });
    // eslint-disable-next-line
  }, [generatedCVs, talents]);

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ------------------ Génération Automatique d'un CV Anonymisé --------------------
  const handleGenerateCVAuto = async (talent) => {
    setAutoGenLoading(true);
    setIaModal({ open: true, talent });
    try {
      // 1. Récupération du CV original
      const response = await fetch(`${API_BASE}/api/recruteur/uploads/${talent.cvUpload}`);
      if (!response.ok) throw new Error("Impossible de récupérer le CV.");
      const cvBlob = await response.blob();

      // 2. Envoi à l'API corrections pour extraction
      const formData = new FormData();
      formData.append('file', cvBlob, talent.cvUpload);
      formData.append('choice', 'option1');
      const extractionRes = await fetch('https://www.cooptaleo.com/corrections/', {
        method: 'POST',
        body: formData,
      });
      const extractionData = await extractionRes.json();
      if (extractionData.status !== 'success') throw new Error("Erreur lors de l'extraction du CV");

      const corrections = extractionData.data;


      // 4. Génération du PDF anonymisé via API Flask
      const genRes = await fetch('https://www.cooptaleo.com/corrections/user-corrections', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...corrections, choice: 'option1' }),
      });
      if (!genRes.ok) throw new Error("Erreur lors de la génération du PDF");

      const pdfBlob = await genRes.blob();

      // 5. Upload du fichier PDF dans la base
      const formDataPDF = new FormData();
      formDataPDF.append('file', pdfBlob, 'cv_anonymise.pdf');
      formDataPDF.append('model', 'option1');
      formDataPDF.append('talentId', talent._id);
      formDataPDF.append('name', corrections.name || '');
      formDataPDF.append('title', corrections.title || '');
      formDataPDF.append('userId', localStorage.getItem('userId'));
      formDataPDF.append('recruteurName', localStorage.getItem('recruteurName'));

      const uploadRes = await fetch('https://server.cooptaleo.com/api/cv-generated/save', {
        method: 'POST',
        body: formDataPDF,
      });
      if (!uploadRes.ok) throw new Error("Erreur lors de l'upload du CV généré");

      // 6. Mettre à jour l'historique (fetch cv-generated pour ce talent)
      const reload = await fetch(`${API_BASE}/api/cv-generated/list/${talent._id}`);
      const newCVs = await reload.json();
      setGeneratedCVs(prev => ({ ...prev, [talent._id]: newCVs.data }));

      // 7. Sélectionner automatiquement le dernier CV généré
      const sortedCVs = [...newCVs.data].sort((a, b) =>
        new Date(b.generatedAt || b.date || 0) - new Date(a.generatedAt || a.date || 0)
      );
      const latestCvId = sortedCVs[0]?._id;
      setSelectedCVs(prev => ({ ...prev, [talent._id]: latestCvId }));

      setAlert({
        open: true,
        message: "CV anonymisé généré et enregistré avec succès.",
        severity: "success"
      });
    } catch (err) {
      setAlert({ open: true, message: err.message, severity: "error" });
    } finally {
      setIaModal({ open: false, talent: null });
      setAutoGenLoading(false);
    }
  };
  // ----------------------------------------------------------------------------

  // Récapitulatif popup
  const handleOpenRecap = () => {
    const recap = talents.filter(t => selected.includes(t._id));
    setRecapTalents(recap);
    setOpenRecap(true);
  };

  // Envoi de la candidature + refresh envoyés
  const handleConfirmRecapAndSend = async () => {
    setOpenRecap(false);
    await handleSubmit();
    // Rafraîchir la liste des envoyés après l'envoi
    if (mission?._id && userId) {
      const resEnvoyes = await fetch(`${API_BASE}/api/candidatures/envoyees?missionId=${mission._id}&recruteurId=${userId}`);
      const envoyesData = await resEnvoyes.json();
      setEnvoyes(envoyesData);
      setSelected(sel => sel.filter(id => !envoyesData.includes(id)));
    }
  };


useEffect(() => {
  if (mission?._id) {
fetch(`${API_BASE}/api/matching/suggestions/${mission._id}`)
      .then(res => res.json())
      .then(data => {
        const ids = Array.isArray(data) ? data.map(t => t._id) : [];
        setSuggestedTalentIds(ids);
      })
      .catch(console.error);
  }
}, [mission, API_BASE]);


  const handleSubmit = async () => {
    // Validation tarif et devise obligatoires
    let hasTarifError = false;
    let hasDeviseError = false;
    const newErrors = {};
    const missingDeviseIds = [];

    selected.forEach(id => {
      if (!inputTarifs[id] || isNaN(Number(inputTarifs[id])) || Number(inputTarifs[id]) <= 0) {
        newErrors[id] = true;
        hasTarifError = true;
      }
      if (!inputDevises[id] || inputDevises[id] === "") {
        missingDeviseIds.push(id);
        hasDeviseError = true;
      }
    });

    setTarifErrors(newErrors);

    if (hasTarifError) {
      setAlert({ open: true, message: "Veuillez renseigner un tarif pour chaque talent.", severity: "warning" });
      return;
    }

    if (hasDeviseError) {
      setAlert({ open: true, message: "Veuillez sélectionner une devise pour chaque talent.", severity: "warning" });
      return;
    }

    if (!mission || selected.length === 0) {
      setAlert({ open: true, message: "Sélectionnez au moins un candidat.", severity: "warning" });
      return;
    }

    const candidatsSansCV = selected.filter(id =>
      !selectedCVs[id] || !generatedCVs[id] || generatedCVs[id].length === 0
    );
    if (candidatsSansCV.length > 0) {
      setAlert({
        open: true,
        message: "Tous les talents doivent avoir au moins un CV anonymisé pour envoyer leur candidature.",
        severity: "warning"
      });
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/candidatures/envoyer-candidats`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          missionId: mission._id,
          entrepriseId: mission.entrepriseId,
          missionName: mission.titre,
          recruteurId: userId,
          candidats: talents.filter(t => selected.includes(t._id)).map(t => ({
            ...t,
            tarif: inputTarifs[t._id] || t.tarif || null,
            devise: inputDevises[t._id],
            cvId: selectedCVs[t._id],
          }))
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setAlert({ open: true, message: "Candidatures envoyées avec succès.", severity: "success" });
      setSuccessMsg("Une fois la candidature envoyée, vous ne pourrez plus la modifier.");
      setInputDevises({});
      setInputTarifs({});
      setSelectedCVs({});
      setTarifErrors({});
    } catch (err) {
      setAlert({ open: true, message: "Erreur lors de l'envoi.", severity: "error" });
    }
  };


  return (
    <div className="sendcandidats-wrapper">
      <Box className="sendcandidats-header" mb={3}>
        <Typography variant="h6"><strong>Sélectionner un de mes talents</strong></Typography>
        <Box mt={2}>
          <Autocomplete
            fullWidth
            options={Array.isArray(talents) ? talents : []}
            getOptionLabel={(option) => `${option.prenomCandidat || ''} ${option.nomCandidat || ''}`.trim()}
            renderOption={(props, option) => {
              const isSent = envoyes.includes(option._id);
              const isRecrute = option.statut?.toLowerCase() === 'recruté';
              const isDisabled = isSent || isRecrute;

              const statutChip = (
                <Chip
                  label={isSent ? 'Déjà envoyé' : isRecrute ? 'Recruté' : getStatutLabel(option.statut)}
                  size="small"
                  color={isDisabled ? 'default' : 'primary'}
                  style={{ marginLeft: 6 }}
                />
              );

  const isSuggested = suggestedTalentIds.includes(option._id); // 👈 ton tableau de suggestions

  return (
    <li {...props} className={`autocomplete-option ${isDisabled ? 'grayed' : ''}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontWeight: 500 }}>
        {option.prenomCandidat} {option.nomCandidat}
      </span>
      <Box display="flex" gap={1}>
        {isSuggested && (
          <Chip
            label="Suggestion IA"
            size="small"
            color="secondary"
            style={{ fontWeight: 500 }}
          />
        )}
        {statutChip}
      </Box>
    </li>
  );
}}

            onChange={(e, value) => {
              if (!value) return;
              const isSent = envoyes.includes(value?._id);
              const isRecrute = value?.statut?.toLowerCase() === 'recruté';
              if (value && !selected.includes(value._id) && !isSent && !isRecrute) {
                setSelected(prev => [...prev, value._id]);
              }
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Indiquez le nom du candidat à sélectionner" />
            )}
          />

          {selected.length > 0 && (
            <Box className="selected-talents-list-bottom">
              {selected.map((id) => {
                const talent = talents.find(t => t._id === id);
                if (!talent) return null;
                return (
                  <Chip
                    key={id}
                    label={`${talent.prenomCandidat || ''} ${talent.nomCandidat || ''}`}
                    onDelete={() => setSelected(prev => prev.filter(sid => sid !== id))}
                    className="selected-talent-chip"
                  />
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      <Box className="sendcandidats-content">
        <Box mt={3} display="flex" flexDirection="column" alignItems="center" gap={2}>
          <Typography variant="body2" color="textSecondary">ou</Typography>
          <Button variant="outlined" onClick={() => setOpenModal(true)} startIcon={<AddIcon />}>
            Ajouter un nouveau candidat
          </Button>
        </Box>

        {/* --- Liste de cartes au lieu du tableau --- */}
        <Box mt={4} className="cartes-candidats-list">
          {talents.filter(t => selected.includes(t._id)).map(talent => {
            const cvs = generatedCVs[talent._id] || [];
            const sortedCVs = [...cvs].sort((a, b) =>
              new Date(b.generatedAt || b.date || 0) - new Date(a.generatedAt || a.date || 0)
            );
            const defaultCvId = sortedCVs.length > 0 ? sortedCVs[0]._id : "";

            return (
              <Box className={`talent-card ${selected.includes(talent._id) ? "card-selected" : ""}`} key={talent._id}>
                <Box className="card-header">
                  <Typography className="talent-card-name">
                    <a
                      href={`/Recruteur/talent-detail/${talent._id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="nom-prenom-link"
                      style={{ textDecoration: 'none', color: '#185bb6', fontWeight: 500 }}
                    >
                      {talent.prenomCandidat} {talent.nomCandidat}
                    </a>
                  </Typography>
<Box display="flex" alignItems="center" gap={1}>
  {suggestedTalentIds.includes(talent._id) && (
    <Chip
      label="Suggestion IA"
      color="secondary"
      size="small"
      style={{ fontWeight: 500 }}
    />
  )}
  <span className="talent-statut-badge">{getStatutLabel(talent.statut)}</span>
</Box>
                </Box>
                <Box
                  className="card-remove-btn"
                  onClick={() => setSelected(prev => prev.filter(sid => sid !== talent._id))}
                  title="Retirer ce talent"
                >
                  <CloseIcon fontSize="small" />
                </Box>

                <Box className="card-fields">
                  <Box className="field-col">
                    <label className="input-label">Tarif <span style={{ color: '#cc2d2d' }}>*</span></label>
                    <TextField
                      variant="outlined"
                      size="small"
                      type="number"
                      placeholder="Ex : 500"
                      value={inputTarifs[talent._id] ?? talent.tarif ?? ""}
                      onChange={e =>
                        setInputTarifs(prev => ({ ...prev, [talent._id]: e.target.value }))
                      }
                      error={!!tarifErrors[talent._id]}
                      InputProps={{
                        startAdornment: (
                          <EuroIcon fontSize="small" style={{ color: "#1976d2", marginRight: 4 }} />
                        )
                      }}
                      className="card-tarif"
                    />
                  <label className="input-label">Devise <span style={{ color: '#cc2d2d' }}>*</span></label>
                  <FormControl size="small" className="card-devise" fullWidth>
                    <Select
                      value={inputDevises[talent._id] || ""}
                      onChange={e =>
                        setInputDevises(prev => ({ ...prev, [talent._id]: e.target.value }))
                      }
                      displayEmpty
                    >
                      <MenuItem value="" disabled>Sélectionnez une devise</MenuItem>
                      {DEVISES.map(dev => (
                        <MenuItem key={dev.value} value={dev.value}>{dev.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  </Box>
                  <Box className="field-col" style={{ flex: 1 }}>
                    <label className="input-label">Profil anonymisé</label>
                    {sortedCVs.length === 0 ? (
                      <Box className="no-cv-box">
                        <span>Ce talent ne possède pas de profil anonymisé.</span>
                        <Button
                          variant="outlined"
                          size="small"
                          style={{ marginTop: 6, maxWidth: 230 }}
                          disabled={autoGenLoading}
                          onClick={() => handleGenerateCVAuto(talent)}
                        >
                          Anonymiser maintenant
                        </Button>
                      </Box>
                    ) : (
                      <FormControl size="small" className="card-cv" fullWidth>
                        <Select
                          value={selectedCVs[talent._id] ?? defaultCvId}
                          onChange={e =>
                            setSelectedCVs(prev => ({ ...prev, [talent._id]: e.target.value }))
                          }
                          displayEmpty
                        >
                          {sortedCVs.map(cv => {
                            const fileUrl =
                              cv.fileUrl && cv.fileUrl.startsWith('http')
                                ? cv.fileUrl
                                : `${UPLOADS_URL}${cv.fileUrl || cv.name || ''}`;
                            return (
                              <MenuItem
                                key={cv._id}
                                value={cv._id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  justifyContent: 'space-between'
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <DescriptionIcon fontSize="small" style={{ color: "#1976d2", marginRight: 6 }} />
                                  <span style={{ fontWeight: 500 }}>{cv.name}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                  {fileUrl && (
                                    <a
                                      href={fileUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        color: "#1976d2",
                                        display: "flex",
                                        alignItems: "center",
                                        textDecoration: "none",
                                      }}
                                      onClick={e => e.stopPropagation()}
                                      title="Voir le CV anonymisé"
                                    >
                                      <VisibilityOutlinedIcon style={{ fontSize: 22 }} />
                                    </a>
                                  )}
                                  <small style={{ color: '#666', minWidth: 120 }}>
                                    Généré le {formatDate(cv.generatedAt)}
                                  </small>
                                </div>
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                </Box>
                {sortedCVs.length === 0 && (
                  <Box style={{ color: "#cc2d2d", fontSize: "0.97em", marginTop: 7 }}>
                    Impossible d'envoyer la candidature sans CV anonymisé.
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        <Box mt={3} textAlign="center">
          <Button onClick={handleOpenRecap} variant="contained">
            Envoyer la candidature
          </Button>
          {successMsg && (
            <Typography variant="body2" color="success.main" style={{ marginTop: 12, fontWeight: 500 }}>
              {successMsg}
            </Typography>
          )}
        </Box>
      </Box>

      <CustomAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert(prev => ({ ...prev, open: false }))}
      />

      <AjouterTalentModal open={openModal} onClose={() => setOpenModal(false)} />

      {/* Modal de chargement IA */}
      <Dialog
        open={iaModal.open}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: 15, textAlign: 'center', padding: 18 } }}
      >
        <DialogContent style={{ background: "#f7fbff" }}>
          <SmartToyIcon style={{ fontSize: 38, color: "#1976d2" }} />
          <Typography variant="h6" style={{ margin: "18px 0 7px" }}>
            Génération du profil anonymisé...
          </Typography>
          <CircularProgress style={{ margin: "15px 0" }} />
          <Typography variant="body2" style={{ marginTop: 10 }}>
            Merci de rester connecté(e) le temps que l'IA génère le profil anonymisé du talent.
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Modal recap avant envoi */}
      <Dialog
        open={openRecap}
        onClose={() => setOpenRecap(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 15 } }}
      >
        <DialogContent>
          <Typography variant="h6" gutterBottom>Récapitulatif de l’envoi</Typography>
          <Typography variant="body2" color="error" style={{ marginBottom: 8 }}>
            Une fois la candidature envoyée, vous ne pourrez plus la modifier.
          </Typography>
          {recapTalents.map(talent => (
            <Box key={talent._id} mb={2} style={{ borderBottom: "1px solid #e3e3e3", paddingBottom: 7 }}>
              <Typography>
                <b>{talent.prenomCandidat} {talent.nomCandidat}</b>
              </Typography>
              <Typography variant="body2">
                Tarif : <b>{inputTarifs[talent._id]}</b> {inputDevises[talent._id] || "Non défini"}
              </Typography>
              <Typography variant="body2">
                Profil anonymisé :
                <b>
                  {" "}
                  {(
                    (generatedCVs[talent._id] || []).find(cv => cv._id === selectedCVs[talent._id])
                  )?.name || "Non défini"}
                </b>
              </Typography>
            </Box>
          ))}
          <Box mt={2} textAlign="center">
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmRecapAndSend}
            >
              Confirmer l’envoi
            </Button>
            <Button
              style={{ marginLeft: 10 }}
              onClick={() => setOpenRecap(false)}
            >
              Annuler
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SendCandidats;
